import { useMemo, useState } from 'react'
import { X, Plus } from 'lucide-react'

// Multi-select tag chooser with inline creation (#116). Controlled —
// `value` is a list of slugs the parent owns; new typed-in names are
// surfaced via `pendingNames` so the parent can POST them at submit time.
//
// Slug normalisation here mirrors the server's TagController.slugify so
// pending names line up with whatever the server accepts. Two values that
// produce the same slug collapse to one selection.
export function slugify(input) {
  return String(input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function TagPicker({
  value = [],
  onChange = () => {},
  pendingNames = [],
  onPendingChange = () => {},
  availableTags = [],
  maxTags = 10,
}) {
  const [query, setQuery] = useState('')

  const bySlug = useMemo(() => {
    const m = new Map()
    for (const t of availableTags) m.set(t.slug, t)
    return m
  }, [availableTags])

  const totalSelected = value.length
  const atCap = totalSelected >= maxTags
  const querySlug = slugify(query)

  const suggestions = useMemo(() => {
    if (atCap) return []
    const selected = new Set(value)
    return availableTags
      .filter((t) => !selected.has(t.slug))
      .filter((t) => {
        if (!query) return true
        const q = query.toLowerCase()
        return t.name.toLowerCase().includes(q) || t.slug.includes(q)
      })
      .slice(0, 8)
  }, [availableTags, value, query, atCap])

  const canCreate = !atCap
    && querySlug
    && !value.includes(querySlug)
    && !bySlug.has(querySlug)
    && !pendingNames.some((n) => slugify(n) === querySlug)

  const addExisting = (slug) => {
    if (value.includes(slug)) return
    if (atCap) return
    onChange([...value, slug])
    setQuery('')
  }

  const addPending = (name) => {
    const trimmed = name.trim()
    if (!trimmed) return
    if (atCap) return
    onPendingChange([...pendingNames, trimmed])
    setQuery('')
  }

  const removeExisting = (slug) => {
    onChange(value.filter((s) => s !== slug))
  }

  const removePending = (idx) => {
    onPendingChange(pendingNames.filter((_, i) => i !== idx))
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Prefer exact-slug match against an existing tag, then create.
      if (querySlug && bySlug.has(querySlug)) {
        addExisting(querySlug)
      } else if (canCreate) {
        addPending(query)
      }
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((slug) => {
          const tag = bySlug.get(slug)
          const label = tag?.name || slug
          return (
            <span
              key={`s-${slug}`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800"
            >
              {label}
              <button
                type="button"
                onClick={() => removeExisting(slug)}
                aria-label={`Remove ${label}`}
                className="ml-0.5 inline-flex items-center text-brand-700 hover:text-rose-700"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          )
        })}
        {pendingNames.map((name, idx) => (
          <span
            key={`p-${idx}`}
            title="New tag — will be created on save"
            className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800"
          >
            {name}
            <button
              type="button"
              onClick={() => removePending(idx)}
              aria-label={`Remove ${name}`}
              className="ml-0.5 inline-flex items-center text-emerald-700 hover:text-rose-700"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        {totalSelected === 0 && pendingNames.length === 0 ? (
          <span className="text-xs text-slate-400">No tags selected</span>
        ) : null}
      </div>

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={atCap}
          placeholder={atCap ? `At cap (${maxTags} max)` : 'Type to search or add a tag…'}
          aria-label="Search or add a tag"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
        {(suggestions.length > 0 || canCreate) && query ? (
          <ul className="absolute left-0 right-0 z-10 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg">
            {suggestions.map((t) => (
              <li key={`opt-${t.slug}`}>
                <button
                  type="button"
                  onClick={() => addExisting(t.slug)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-brand-50"
                >
                  <span>{t.name}</span>
                  <span className="text-xs text-slate-400">{t.slug}</span>
                </button>
              </li>
            ))}
            {canCreate ? (
              <li>
                <button
                  type="button"
                  onClick={() => addPending(query)}
                  className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Create &quot;{query.trim()}&quot;</span>
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      <div className="text-xs text-slate-500">
        {totalSelected + pendingNames.length} / {maxTags} tags
        {atCap ? ' — remove one to add another' : ''}
      </div>
    </div>
  )
}
